import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { projectAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  GanttChart,
  Plus,
  FolderOpen,
  Calendar,
  LogOut,
  MoreHorizontal,
  Trash2,
  Edit,
  Shield,
  User,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { formatDate } from '../lib/utils';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);
  
  // Check if user is admin
  const isAdmin = user?.is_admin || user?.email?.toLowerCase() === 'agiprojects24@gmail.com';

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await projectAPI.getAll();
      setProjects(res.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }

    setCreating(true);
    try {
      const res = await projectAPI.create(newProject);
      setProjects([...projects, res.data]);
      setCreateOpen(false);
      setNewProject({ name: '', description: '' });
      toast.success('Project created successfully!');
      navigate(`/project/${res.data.id}`);
    } catch (error) {
      toast.error('Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await projectAPI.delete(id);
      setProjects(projects.filter(p => p.id !== id));
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <GanttChart className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl text-zinc-900">ProjectOS</span>
          </Link>
          
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Link to="/admin">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="admin-dashboard-btn"
                  className="text-purple-700 border-purple-200 hover:bg-purple-50"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              </Link>
            )}
            <span className="text-sm text-zinc-600">
              Welcome, <span className="font-medium text-zinc-900">{user?.name}</span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-zinc-600 hover:text-zinc-900"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-1">
              {isAdmin ? 'All Projects' : 'Your Projects'}
            </h1>
            <p className="text-zinc-600">
              {isAdmin 
                ? 'Admin view: Managing all projects across the platform'
                : 'Manage and track all your projects in one place'}
            </p>
          </div>
          
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-project-btn"
                className="bg-primary hover:bg-orange-600 text-white rounded-lg px-6"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-outfit">Create New Project</DialogTitle>
                <DialogDescription>Add a new project to your workspace</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="project-name">Project Name</Label>
                  <Input
                    id="project-name"
                    placeholder="Enter project name"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    data-testid="project-name-input"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project-desc">Description (optional)</Label>
                  <Textarea
                    id="project-desc"
                    placeholder="Brief description of the project"
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    data-testid="project-description-input"
                    className="rounded-xl resize-none"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCreateOpen(false)}
                    className="rounded-lg"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={creating}
                    data-testid="create-project-submit-btn"
                    className="bg-primary hover:bg-orange-600 text-white rounded-lg"
                  >
                    {creating ? <span className="spinner" /> : 'Create Project'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : projects.length === 0 ? (
          <div className="empty-state py-20">
            <div className="w-20 h-20 bg-zinc-100 rounded-full flex items-center justify-center mb-6">
              <FolderOpen className="w-10 h-10 text-zinc-400" />
            </div>
            <h3 className="font-outfit text-xl font-semibold text-zinc-900 mb-2">
              No projects yet
            </h3>
            <p className="text-zinc-600 mb-6 max-w-md">
              Create your first project to start planning with Gantt charts, Kanban boards, and more.
            </p>
            <Button
              onClick={() => setCreateOpen(true)}
              data-testid="empty-create-project-btn"
              className="bg-primary hover:bg-orange-600 text-white rounded-lg px-6"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-stagger">
            {projects.map((project) => (
              <Card
                key={project.id}
                className="bg-white border-zinc-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 group"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                      <GanttChart className="w-5 h-5 text-primary" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`project-menu-${project.id}`}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => navigate(`/project/${project.id}`)}
                          data-testid={`edit-project-${project.id}`}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Open Project
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteProject(project.id)}
                          className="text-red-600"
                          data-testid={`delete-project-${project.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <Link to={`/project/${project.id}`} data-testid={`project-card-${project.id}`}>
                    <h3 className="font-outfit font-semibold text-lg text-zinc-900 mb-2 hover:text-primary transition-colors">
                      {project.name}
                    </h3>
                  </Link>
                  
                  <p className="text-zinc-600 text-sm mb-4 line-clamp-2">
                    {project.description || 'No description'}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-zinc-500">
                    <div className="flex items-center">
                      <Calendar className="w-3.5 h-3.5 mr-1.5" />
                      Created {formatDate(project.created_at)}
                    </div>
                    {isAdmin && project.owner_id !== user?.id && (
                      <div className="flex items-center text-purple-600">
                        <User className="w-3.5 h-3.5 mr-1" />
                        {project.owner_name || project.owner_email}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
